import { createContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { SimpleDataCacheService } from '@/lib/utils/simple-data-cache-service';
import { sortHistoricalEvents } from './historical-events-data-service';

interface HistoricalEventsDataContextType {
  historicalEvents: HistoricalEvent[];
  getHistoricalEventById: (id: string) => HistoricalEvent | undefined;
  getHistoricalEventByTitle: (title: string) => HistoricalEvent | undefined;
}

export const HistoricalEventsDataContext = createContext<HistoricalEventsDataContextType | undefined>(undefined);

interface HistoricalEventsDataProviderProps {
  children: ReactNode;
  historicalEvents: HistoricalEvent[];
}

export function HistoricalEventsDataProvider({ children, historicalEvents: initialData }: HistoricalEventsDataProviderProps) {
  const [cacheService] = useState(() =>
    new SimpleDataCacheService<HistoricalEvent>(
      initialData,
      sortHistoricalEvents
    )
  );

  // Track items in state to trigger re-renders when data changes
  const [items, setItems] = useState<HistoricalEvent[]>(() => cacheService.getItems());

  useEffect(() => {
    cacheService.setNewItems(initialData);
    setItems(cacheService.getItems());
  }, [initialData, cacheService]);

  const getHistoricalEventById = (id: string): HistoricalEvent | undefined => {
    return items.find(item => item.id === id);
  };

  const getHistoricalEventByTitle = (title: string): HistoricalEvent | undefined => {
    return items.find(item => item.title === title);
  };

  return (
    <HistoricalEventsDataContext.Provider
      value={{
        historicalEvents: items,
        getHistoricalEventById,
        getHistoricalEventByTitle
      }}
    >
      {children}
    </HistoricalEventsDataContext.Provider>
  );
}

